import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, Clock, Users, Calendar, AlertTriangle, Award, BarChart3, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

export default function DashboardGraficosContent() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('30');
  const [horasTrabalhadas, setHorasTrabalhadas] = useState([]);
  const [bancoHorasEvolucao, setBancoHorasEvolucao] = useState([]);
  const [metricasSetor, setMetricasSetor] = useState([]);
  const [metricasCargo, setMetricasCargo] = useState([]);
  const [topHorasExtras, setTopHorasExtras] = useState([]);
  const [resumoSemanal, setResumoSemanal] = useState(null);

  const empresaId = user?.empresa_id;

  useEffect(() => {
    if (token && empresaId) {
      fetchAllData();
    }
  }, [token, empresaId, periodo]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [horasRes, bancoRes, setorRes, cargoRes, topRes, resumoRes] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/horas-trabalhadas?empresa_id=${empresaId}&periodo=${periodo}`, { headers }),
        fetch(`${API_URL}/api/dashboard/banco-horas-evolucao?empresa_id=${empresaId}&meses=6`, { headers }),
        fetch(`${API_URL}/api/dashboard/metricas-setor?empresa_id=${empresaId}`, { headers }),
        fetch(`${API_URL}/api/dashboard/metricas-cargo?empresa_id=${empresaId}`, { headers }),
        fetch(`${API_URL}/api/dashboard/top-horas-extras?empresa_id=${empresaId}&limit=10`, { headers }),
        fetch(`${API_URL}/api/dashboard/resumo-semanal?empresa_id=${empresaId}`, { headers })
      ]);

      if (horasRes.ok) {
        const data = await horasRes.json();
        setHorasTrabalhadas(data.dados || []);
      }
      if (bancoRes.ok) {
        const data = await bancoRes.json();
        setBancoHorasEvolucao(data.evolucao || []);
      }
      if (setorRes.ok) {
        const data = await setorRes.json();
        setMetricasSetor(data.metricas || []);
      }
      if (cargoRes.ok) {
        const data = await cargoRes.json();
        setMetricasCargo(data.metricas || []);
      }
      if (topRes.ok) {
        const data = await topRes.json();
        setTopHorasExtras(data.ranking || []);
      }
      if (resumoRes.ok) {
        setResumoSemanal(await resumoRes.json());
      }
    } catch (error) {
      toast.error('Erro ao carregar dados dos gráficos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-graficos-content">
      {/* Filtro de período */}
      <div className="flex justify-end">
        <Select value={periodo} onValueChange={setPeriodo}>
          <SelectTrigger className="w-40" data-testid="select-periodo-graficos">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 dias</SelectItem>
            <SelectItem value="15">Últimos 15 dias</SelectItem>
            <SelectItem value="30">Últimos 30 dias</SelectItem>
            <SelectItem value="60">Últimos 60 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Resumo Semanal */}
      {resumoSemanal && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <Clock className="w-5 h-5 mb-1 opacity-80" />
              <p className="text-2xl font-bold">{resumoSemanal.totais?.horas_trabalhadas || 0}h</p>
              <p className="text-xs opacity-80">Horas Trabalhadas</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <TrendingUp className="w-5 h-5 mb-1 opacity-80" />
              <p className="text-2xl font-bold">{resumoSemanal.totais?.horas_extras || 0}h</p>
              <p className="text-xs opacity-80">Horas Extras</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-4">
              <Users className="w-5 h-5 mb-1 opacity-80" />
              <p className="text-2xl font-bold">{resumoSemanal.totais?.empregados_ativos || 0}</p>
              <p className="text-xs opacity-80">Empregados Ativos</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
            <CardContent className="p-4">
              <AlertTriangle className="w-5 h-5 mb-1 opacity-80" />
              <p className="text-2xl font-bold">{resumoSemanal.totais?.total_faltas || 0}</p>
              <p className="text-xs opacity-80">Faltas</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <Calendar className="w-5 h-5 mb-1 opacity-80" />
              <p className="text-2xl font-bold">{resumoSemanal.totais?.total_atrasos || 0}</p>
              <p className="text-xs opacity-80">Atrasos</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
            <CardContent className="p-4">
              <BarChart3 className="w-5 h-5 mb-1 opacity-80" />
              <p className="text-2xl font-bold">{resumoSemanal.totais?.ocorrencias || 0}</p>
              <p className="text-xs opacity-80">Ocorrências</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-600 to-slate-700 text-white">
            <CardContent className="p-4">
              <Clock className="w-5 h-5 mb-1 opacity-80" />
              <p className="text-2xl font-bold">{resumoSemanal.totais?.marcacoes_hoje || 0}</p>
              <p className="text-xs opacity-80">Marcações Hoje</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="horas" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="horas">Horas</TabsTrigger>
          <TabsTrigger value="banco">Banco de Horas</TabsTrigger>
          <TabsTrigger value="setor">Por Setor</TabsTrigger>
          <TabsTrigger value="cargo">Por Cargo</TabsTrigger>
          <TabsTrigger value="ranking">Ranking</TabsTrigger>
        </TabsList>

        {/* Horas Trabalhadas */}
        <TabsContent value="horas">
          <Card data-testid="chart-horas-trabalhadas">
            <CardHeader>
              <CardTitle>Horas Trabalhadas por Dia</CardTitle>
              <CardDescription>Evolução das horas normais e extras no período</CardDescription>
            </CardHeader>
            <CardContent>
              {horasTrabalhadas.length === 0 ? (
                <div className="h-80 flex items-center justify-center text-slate-500">
                  Sem dados para exibir no período selecionado
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={horasTrabalhadas}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="data"
                      tickFormatter={(value) => value?.slice(5) || ''}
                      fontSize={12}
                    />
                    <YAxis fontSize={12} />
                    <Tooltip
                      formatter={(value) => [`${value}h`, '']}
                      labelFormatter={(label) => `Data: ${label}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="horas_normais"
                      name="Horas Normais"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="horas_extras"
                      name="Horas Extras"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Banco de Horas */}
        <TabsContent value="banco">
          <Card data-testid="chart-banco-horas">
            <CardHeader>
              <CardTitle>Evolução do Banco de Horas</CardTitle>
              <CardDescription>Créditos, débitos e saldo dos últimos 6 meses</CardDescription>
            </CardHeader>
            <CardContent>
              {bancoHorasEvolucao.length === 0 ? (
                <div className="h-80 flex items-center justify-center text-slate-500">
                  Sem dados de banco de horas
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={bancoHorasEvolucao}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="periodo" fontSize={12} />
                    <YAxis fontSize={12} />
                    <Tooltip formatter={(value) => [`${value}h`, '']} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="creditos"
                      name="Créditos"
                      stackId="1"
                      stroke="#10b981"
                      fill="#10b98133"
                    />
                    <Area
                      type="monotone"
                      dataKey="debitos"
                      name="Débitos"
                      stackId="2"
                      stroke="#ef4444"
                      fill="#ef444433"
                    />
                    <Area
                      type="monotone"
                      dataKey="saldo"
                      name="Saldo"
                      stroke="#3b82f6"
                      fill="#3b82f633"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Métricas por Setor */}
        <TabsContent value="setor">
          <Card data-testid="chart-metricas-setor">
            <CardHeader>
              <CardTitle>Métricas por Setor</CardTitle>
              <CardDescription>Horas trabalhadas e extras por setor</CardDescription>
            </CardHeader>
            <CardContent>
              {metricasSetor.length === 0 ? (
                <div className="h-80 flex items-center justify-center text-slate-500">
                  Sem dados de setores
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={metricasSetor} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" fontSize={12} />
                    <YAxis dataKey="setor" type="category" width={120} fontSize={12} />
                    <Tooltip formatter={(value) => [`${value}h`, '']} />
                    <Legend />
                    <Bar dataKey="horas_trabalhadas" name="Horas Trabalhadas" fill="#3b82f6" />
                    <Bar dataKey="horas_extras" name="Horas Extras" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Métricas por Cargo */}
        <TabsContent value="cargo">
          <Card data-testid="chart-metricas-cargo">
            <CardHeader>
              <CardTitle>Distribuição por Cargo</CardTitle>
              <CardDescription>Quantidade de empregados por cargo</CardDescription>
            </CardHeader>
            <CardContent>
              {metricasCargo.length === 0 ? (
                <div className="h-80 flex items-center justify-center text-slate-500">
                  Sem dados de cargos
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={metricasCargo}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ cargo, total_empregados }) => `${cargo}: ${total_empregados}`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="total_empregados"
                        nameKey="cargo"
                      >
                        {metricasCargo.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [value, name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ranking Horas Extras */}
        <TabsContent value="ranking">
          <Card data-testid="chart-ranking-extras">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Top 10 - Horas Extras
              </CardTitle>
              <CardDescription>Empregados com mais horas extras no mês</CardDescription>
            </CardHeader>
            <CardContent>
              {topHorasExtras.length === 0 ? (
                <div className="h-80 flex items-center justify-center text-slate-500">
                  Sem dados de horas extras
                </div>
              ) : (
                <div className="space-y-3">
                  {topHorasExtras.map((emp, idx) => (
                    <div
                      key={emp.empregado_id}
                      className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg"
                      data-testid={`ranking-item-${idx}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        idx === 0 ? 'bg-amber-500 text-white' :
                        idx === 1 ? 'bg-slate-400 text-white' :
                        idx === 2 ? 'bg-amber-700 text-white' :
                        'bg-slate-200 text-slate-600'
                      }`}>
                        {emp.posicao}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{emp.nome}</p>
                        <p className="text-sm text-slate-500">{emp.cargo} • {emp.setor}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-green-600">{emp.horas_extras}h</p>
                        <p className="text-xs text-slate-500">
                          {emp.dias_trabalhados} dias • {emp.media_diaria_extras}h/dia
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Breakdown Semanal */}
      {resumoSemanal?.dias && (
        <Card data-testid="chart-semana">
          <CardHeader>
            <CardTitle>Esta Semana</CardTitle>
            <CardDescription>
              {resumoSemanal.semana?.inicio} a {resumoSemanal.semana?.fim}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={resumoSemanal.dias}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="dia_semana"
                  tickFormatter={(value) => value?.slice(0, 3) || ''}
                  fontSize={12}
                />
                <YAxis fontSize={12} />
                <Tooltip />
                <Legend />
                <Bar dataKey="horas_trabalhadas" name="Horas" fill="#3b82f6" />
                <Bar dataKey="horas_extras" name="Extras" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
